/**
 * Kontoauszug einlesen und Umsaetze zuordnen.
 *
 * Oben die Ablage fuer die CAMT-Datei mit dem Ergebnis des letzten
 * Einlesens, darunter die Umsaetze nach Status: Vorschlaege zuerst, denn
 * dort wartet Arbeit; Unklares zum Zuordnen von Hand; Gebuchtes und
 * Uebergangenes zum Nachsehen.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatAmount, formatDate } from '@erp/shared';

import { api, ApiError, apiUrl, withQuery } from '../api/client';
import type { ListResponse, Partner } from '../api/types';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { SearchSelect } from '../components/SearchSelect';
import { useToast } from '../components/toast';
import { Alert, Badge, Dialog, EmptyState, Field, LoadingBlock, NumericInput, TextInput } from '../components/ui';

type Status = 'suggested' | 'unmatched' | 'matched' | 'ignored';

type Transaction = {
  id: string;
  bookingDate: string;
  valueDate: string | null;
  amount: number;
  direction: 'incoming' | 'outgoing';
  counterpartName: string | null;
  counterpartIban: string | null;
  purpose: string | null;
  bookingText: string | null;
  status: Status;
  suggestedPartnerId: string | null;
  suggestedPartnerName: string | null;
  suggestedDocumentId: string | null;
  suggestedDocumentNumber: string | null;
  suggestedDocumentOpen: number | null;
  suggestedDiscount: number;
  matchReason: string | null;
  paymentReference: string | null;
};

type Statement = {
  id: string;
  fileName: string;
  format: string;
  accountIban: string | null;
  fromDate: string | null;
  toDate: string | null;
  entryCount: number;
  importedCount: number;
  createdAt: string;
};

type ImportSummary = {
  statement: Statement;
  imported: number;
  skipped: number;
  matched: number;
  suggested: number;
  unmatched: number;
};

type Candidate = {
  id: string;
  number: string | null;
  externalReference: string | null;
  documentDate: string;
  dueDate: string | null;
  grossTotal: number;
  paidAmount: number;
  partnerId: string;
  partnerName: string;
  open: number;
  exact: boolean;
};

const STATUS_LABELS: Record<Status, string> = {
  suggested: 'Vorschläge',
  unmatched: 'Unklar',
  matched: 'Gebucht',
  ignored: 'Übergangen',
};

const STATUS_BADGE: Record<Status, 'warning' | 'neutral' | 'success' | 'accent'> = {
  suggested: 'warning',
  unmatched: 'neutral',
  matched: 'success',
  ignored: 'accent',
};

function maskIban(iban: string | null): string {
  if (!iban) return '';
  return iban.length > 8 ? `${iban.slice(0, 4)} …${iban.slice(-4)}` : iban;
}

export function BankStatementsPage() {
  const { can } = useAuth();
  const mayBook = can('accounting:write');
  const queryClient = useQueryClient();
  const toast = useToast();

  const [status, setStatus] = useState<Status>('suggested');
  const [search, setSearch] = useState('');
  const [lastImport, setLastImport] = useState<ImportSummary | null>(null);
  const [assigning, setAssigning] = useState<Transaction | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const transactions = useQuery({
    queryKey: ['bank-transactions', status, search],
    queryFn: () =>
      api.get<{ items: Transaction[]; total: number; counts: Record<string, number> }>(
        withQuery('/api/accounting/bank-transactions', { status, q: search || undefined, limit: 200 }),
      ),
  });

  const statements = useQuery({
    queryKey: ['bank-statements'],
    queryFn: () => api.get<{ statements: Statement[] }>('/api/accounting/bank-statements'),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
    queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
    queryClient.invalidateQueries({ queryKey: ['open-items'] });
    queryClient.invalidateQueries({ queryKey: ['payments'] });
    queryClient.invalidateQueries({ queryKey: ['journal'] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  }

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      const response = await fetch(apiUrl('/api/accounting/bank-statements'), {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(response.status, 'UPLOAD', payload.message ?? 'Die Datei konnte nicht eingelesen werden.');
      }
      return (await response.json()) as ImportSummary;
    },
    onSuccess: (summary) => {
      setLastImport(summary);
      setStatus(summary.suggested > 0 ? 'suggested' : summary.unmatched > 0 ? 'unmatched' : 'matched');
      refresh();
      toast.success(
        `${summary.imported} Umsätze eingelesen: ${summary.matched} gebucht, ${summary.suggested} Vorschläge, ${summary.unmatched} unklar.`,
      );
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Das Einlesen ist fehlgeschlagen.'),
  });

  const act = useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: 'confirm' | 'ignore' | 'reopen'; note?: string }) =>
      api.post(`/api/accounting/bank-transactions/${id}/${action}`, note ? { note } : {}),
    onSuccess: (_, variables) => {
      refresh();
      toast.success(
        variables.action === 'confirm'
          ? 'Die Zahlung wurde gebucht.'
          : variables.action === 'ignore'
            ? 'Der Umsatz wurde übergangen.'
            : 'Der Umsatz ist wieder in Bearbeitung.',
      );
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'Die Aktion ist fehlgeschlagen.'),
  });

  // Seitenweite Ablage: Datei irgendwo auf die Seite ziehen.
  useEffect(() => {
    if (!mayBook) return;
    const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files');
    const onDragOver = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      setDragging(true);
    };
    const onDragLeave = (event: DragEvent) => {
      if (event.relatedTarget === null) setDragging(false);
    };
    const onDrop = (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      setDragging(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) upload.mutate(file);
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mayBook]);

  const counts = transactions.data?.counts ?? {};
  const items = transactions.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Kontoauszug"
        subtitle="CAMT-Datei aus dem Online-Banking einlesen, Zahlungen automatisch zuordnen"
        actions={
          mayBook ? (
            <>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => fileInput.current?.click()}
                disabled={upload.isPending}
              >
                {upload.isPending ? 'Wird eingelesen …' : 'Kontoauszug einlesen …'}
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".xml,application/xml,text/xml"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) upload.mutate(file);
                  event.target.value = '';
                }}
              />
            </>
          ) : undefined
        }
      />

      {dragging ? (
        <div className="drop-overlay">
          <div className="drop-overlay-box">
            <strong>Datei loslassen</strong>
            <span>CAMT-Kontoauszug einlesen</span>
          </div>
        </div>
      ) : null}

      {lastImport ? (
        <Alert kind={lastImport.suggested + lastImport.unmatched === 0 ? 'success' : 'info'}>
          <strong>{lastImport.statement.fileName}</strong>: {lastImport.imported} Umsätze neu
          {lastImport.skipped > 0 ? `, ${lastImport.skipped} bereits bekannt` : ''}.{' '}
          {lastImport.matched} automatisch gebucht, {lastImport.suggested} Vorschläge,{' '}
          {lastImport.unmatched} unklar.
        </Alert>
      ) : null}

      <div className="row" style={{ marginBottom: 12, gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {(Object.keys(STATUS_LABELS) as Status[]).map((key) => (
          <button
            key={key}
            type="button"
            className={`btn btn-sm${status === key ? ' btn-primary' : ''}`}
            onClick={() => setStatus(key)}
          >
            {STATUS_LABELS[key]}
            {counts[key] ? ` (${counts[key]})` : ''}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ width: 260 }}>
          <TextInput value={search} onChange={setSearch} placeholder="Name, IBAN oder Verwendungszweck …" />
        </div>
      </div>

      <div className="card">
        {transactions.isLoading ? <LoadingBlock /> : null}
        {transactions.data && items.length === 0 ? (
          <EmptyState
            title={
              status === 'suggested'
                ? 'Keine Vorschläge offen'
                : status === 'unmatched'
                  ? 'Nichts Unklares'
                  : `Keine Umsätze mit Status „${STATUS_LABELS[status]}"`
            }
            hint={
              statements.data && statements.data.statements.length === 0
                ? 'Noch kein Kontoauszug eingelesen. Datei wählen oder auf die Seite ziehen.'
                : undefined
            }
          />
        ) : null}
        {items.length > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Gegenpartei</th>
                  <th>Verwendungszweck</th>
                  <th className="num">Betrag</th>
                  <th>Zuordnung</th>
                  {mayBook ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((tx) => (
                  <tr key={tx.id}>
                    <td className="mono nowrap">{formatDate(tx.bookingDate)}</td>
                    <td>
                      <div>{tx.counterpartName ?? <span className="faint">unbekannt</span>}</div>
                      <div className="faint mono" style={{ fontSize: 12 }}>
                        {maskIban(tx.counterpartIban)}
                        {tx.bookingText ? ` · ${tx.bookingText}` : ''}
                      </div>
                    </td>
                    <td style={{ maxWidth: 360, whiteSpace: 'normal' }}>{tx.purpose}</td>
                    <td
                      className="num mono nowrap"
                      style={{ color: tx.direction === 'incoming' ? 'var(--success)' : undefined }}
                    >
                      {tx.direction === 'incoming' ? '+' : '−'}
                      {formatAmount(tx.amount)} €
                    </td>
                    <td style={{ maxWidth: 320, whiteSpace: 'normal' }}>
                      <Badge kind={STATUS_BADGE[tx.status]}>{STATUS_LABELS[tx.status]}</Badge>
                      {tx.suggestedDocumentNumber || tx.suggestedPartnerName ? (
                        <div style={{ marginTop: 4 }}>
                          {tx.suggestedDocumentNumber ? <strong>{tx.suggestedDocumentNumber}</strong> : null}
                          {tx.suggestedDocumentNumber && tx.suggestedPartnerName ? ' · ' : ''}
                          {tx.suggestedPartnerName}
                          {tx.suggestedDiscount > 0 ? ` · Skonto ${formatAmount(tx.suggestedDiscount)} €` : ''}
                        </div>
                      ) : null}
                      {tx.matchReason ? (
                        <div className="faint" style={{ fontSize: 12, marginTop: 2 }}>
                          {tx.matchReason}
                        </div>
                      ) : null}
                    </td>
                    {mayBook ? (
                      <td className="nowrap" style={{ textAlign: 'right' }}>
                        {tx.status === 'suggested' && tx.suggestedDocumentId ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={act.isPending}
                            onClick={() => act.mutate({ id: tx.id, action: 'confirm' })}
                          >
                            Bestätigen
                          </button>
                        ) : null}
                        {tx.status === 'suggested' || tx.status === 'unmatched' ? (
                          <>
                            {' '}
                            <button type="button" className="btn btn-sm" onClick={() => setAssigning(tx)}>
                              Zuordnen …
                            </button>{' '}
                            <button
                              type="button"
                              className="btn btn-sm"
                              disabled={act.isPending}
                              onClick={() => act.mutate({ id: tx.id, action: 'ignore' })}
                              title="Gebühr, Umbuchung, Privates - kein Beleg dazu"
                            >
                              Übergehen
                            </button>
                          </>
                        ) : null}
                        {tx.status === 'ignored' ? (
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={act.isPending}
                            onClick={() => act.mutate({ id: tx.id, action: 'reopen' })}
                          >
                            Zurückholen
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {statements.data && statements.data.statements.length > 0 ? (
        <div className="card">
          <div className="card-header">
            <h2>Eingelesene Auszüge</h2>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Eingelesen</th>
                  <th>Datei</th>
                  <th>Format</th>
                  <th>Konto</th>
                  <th>Zeitraum</th>
                  <th className="num">Umsätze</th>
                  <th className="num">davon neu</th>
                </tr>
              </thead>
              <tbody>
                {statements.data.statements.map((s) => (
                  <tr key={s.id}>
                    <td className="mono nowrap">{formatDate(s.createdAt.slice(0, 10))}</td>
                    <td>{s.fileName}</td>
                    <td className="mono">{s.format}</td>
                    <td className="mono">{maskIban(s.accountIban)}</td>
                    <td className="nowrap">
                      {s.fromDate ? formatDate(s.fromDate) : '–'} – {s.toDate ? formatDate(s.toDate) : '–'}
                    </td>
                    <td className="num mono">{s.entryCount}</td>
                    <td className="num mono">{s.importedCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {assigning ? (
        <AssignDialog
          transaction={assigning}
          onClose={() => setAssigning(null)}
          onBooked={() => {
            setAssigning(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}

// --- Zuordnung von Hand -------------------------------------------------------

function AssignDialog({
  transaction,
  onClose,
  onBooked,
}: {
  transaction: Transaction;
  onClose: () => void;
  onBooked: () => void;
}) {
  const toast = useToast();
  const [partnerId, setPartnerId] = useState(transaction.suggestedPartnerId ?? '');
  const [amounts, setAmounts] = useState<Record<string, { amount: number; discount: number }>>({});
  const [error, setError] = useState<string | null>(null);

  const partnersList = useQuery({
    queryKey: ['partners', 'all', 'bank'],
    queryFn: () => api.get<ListResponse<Partner>>(withQuery('/api/partners', { limit: 500 })),
  });

  const candidates = useQuery({
    queryKey: ['bank-candidates', transaction.id, partnerId],
    queryFn: () =>
      api.get<{ partnerId: string | null; items: Candidate[] }>(
        withQuery(`/api/accounting/bank-transactions/${transaction.id}/candidates`, {
          partnerId: partnerId || undefined,
        }),
      ),
  });

  const allocated = Object.values(amounts).reduce((sum, a) => sum + a.amount, 0);
  const remaining = transaction.amount - allocated;

  const book = useMutation({
    mutationFn: () =>
      api.post(`/api/accounting/bank-transactions/${transaction.id}/book`, {
        partnerId: partnerId || null,
        allocations: Object.entries(amounts)
          .filter(([, a]) => a.amount > 0 || a.discount > 0)
          .map(([documentId, a]) => ({ documentId, amount: a.amount, discountAmount: a.discount })),
      }),
    onSuccess: () => {
      toast.success('Die Zahlung wurde gebucht.');
      onBooked();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Die Zuordnung konnte nicht gebucht werden.'),
  });

  function setAllocation(candidate: Candidate, amount: number, discount = amounts[candidate.id]?.discount ?? 0) {
    setAmounts((current) => ({ ...current, [candidate.id]: { amount, discount } }));
  }

  return (
    <Dialog
      title={`Umsatz zuordnen · ${transaction.direction === 'incoming' ? '+' : '−'}${formatAmount(transaction.amount)} €`}
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={book.isPending || remaining < 0}
            onClick={() => {
              setError(null);
              book.mutate();
            }}
          >
            {allocated === 0 ? 'Ohne Zuordnung buchen' : 'Zuordnen und buchen'}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}

      <div className="definition-list" style={{ marginBottom: 12 }}>
        <dt>Gegenpartei</dt>
        <dd>
          {transaction.counterpartName ?? '–'}{' '}
          <span className="faint mono">{maskIban(transaction.counterpartIban)}</span>
        </dd>
        <dt>Verwendungszweck</dt>
        <dd>{transaction.purpose ?? '–'}</dd>
        <dt>Datum</dt>
        <dd className="mono">{formatDate(transaction.valueDate ?? transaction.bookingDate)}</dd>
      </div>

      <Field
        label="Partner"
        hint="Grenzt die offenen Posten ein. Die IBAN wird beim Buchen am Partner gemerkt, wenn dort noch keine steht."
      >
        <SearchSelect
          value={partnerId}
          onChange={(value) => {
            setPartnerId(value);
            setAmounts({});
          }}
          options={(partnersList.data?.items ?? []).map((p) => ({
            value: p.id,
            label: `${p.number} · ${p.name}`,
          }))}
          placeholder="Name oder Nummer tippen …"
          emptyLabel="Alle offenen Posten"
        />
      </Field>

      {candidates.isLoading ? <LoadingBlock /> : null}
      {candidates.data && candidates.data.items.length === 0 ? (
        <EmptyState
          title="Keine offenen Posten"
          hint="Ohne Zuordnung buchen legt die Zahlung als nicht zugeordnet an; die Zuordnung kann später nachgeholt werden."
        />
      ) : null}
      {candidates.data && candidates.data.items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Beleg</th>
                <th>Partner</th>
                <th>Fällig</th>
                <th className="num">Offen</th>
                <th className="num" style={{ width: 130 }}>
                  Zuordnen
                </th>
                <th className="num" style={{ width: 110 }}>
                  Skonto
                </th>
              </tr>
            </thead>
            <tbody>
              {candidates.data.items.map((candidate) => {
                const current = amounts[candidate.id] ?? { amount: 0, discount: 0 };
                return (
                  <tr key={candidate.id} style={candidate.exact ? { background: 'var(--bg-accent-soft, transparent)' } : undefined}>
                    <td className="mono nowrap">
                      {candidate.number ?? candidate.externalReference}
                      {candidate.exact ? (
                        <>
                          {' '}
                          <Badge kind="success">Betrag passt</Badge>
                        </>
                      ) : null}
                    </td>
                    <td>{candidate.partnerName}</td>
                    <td className="mono nowrap">{candidate.dueDate ? formatDate(candidate.dueDate) : '–'}</td>
                    <td className="num mono nowrap">{formatAmount(candidate.open)} €</td>
                    <td className="num">
                      <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                        <NumericInput
                          kind="amount"
                          value={current.amount}
                          onChange={(value) => setAllocation(candidate, value)}
                        />
                        <button
                          type="button"
                          className="btn btn-sm"
                          title="Offenen Betrag bzw. Restbetrag des Umsatzes übernehmen"
                          onClick={() =>
                            setAllocation(candidate, Math.min(candidate.open - current.discount, remaining + current.amount))
                          }
                        >
                          voll
                        </button>
                      </div>
                    </td>
                    <td className="num">
                      <NumericInput
                        kind="amount"
                        value={current.discount}
                        onChange={(value) => setAllocation(candidate, current.amount, value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="row" style={{ justifyContent: 'flex-end', gap: 16, marginTop: 12 }}>
        <span className="faint">Zugeordnet</span>
        <strong className="mono">{formatAmount(allocated)} €</strong>
        <span className="faint">Rest</span>
        <strong className="mono" style={remaining < 0 ? { color: 'var(--danger)' } : undefined}>
          {formatAmount(remaining)} €
        </strong>
      </div>
      {remaining < 0 ? (
        <Alert kind="error">Es ist mehr zugeordnet, als der Umsatz hergibt.</Alert>
      ) : null}
    </Dialog>
  );
}
